export interface Origem {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  referrer: string | null;
  landing: string | null;
  clickId: string | null;
  firstSeenAt: string;
}

const PRIMEIRO_TOQUE = 'titularis_primeiro_toque';
const TOQUE_ATUAL = 'titularis_toque_atual';
const VALIDADE_EM_SEGUNDOS = 365 * 24 * 60 * 60;
const memoria = new Map<string, Origem>();

function normalizar(valor: string | null): string | null {
  return valor
    ?.normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') || null;
}

function lerUrl(): URL | null {
  try {
    return new URL(globalThis.location.href);
  } catch {
    return null;
  }
}

/**
 * O domínio que se pode registrar, a partir de um host qualquer.
 *
 * `l.instagram.com` e `www.instagram.com` têm o mesmo: é o que permite tratar
 * os dois como Instagram, e é o mesmo recorte que o cookie precisa para
 * atravessar de `titularis.com.br` para `app.titularis.com.br`.
 */
function registravel(hostname: string): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (host === 'localhost' || host.endsWith('.localhost') || host.includes(':')) return null;
  if (/^[\d.]+$/.test(host)) return null;

  const rotulos = host.split('.');
  // `com.br` é sufixo público: os dois últimos rótulos fariam o navegador
  // rejeitar o cookie, justamente na travessia entre a landing e o app.
  const quantidade = host.endsWith('.com.br') ? 3 : 2;
  return rotulos.length >= quantidade ? rotulos.slice(-quantidade).join('.') : null;
}

/**
 * **Nós mesmos não somos um canal.**
 *
 * O visitante que clica da landing para o app chega lá com
 * `referrer: www.titularis.com.br`, e sem esta guarda o app gravaria isso como
 * a origem da pessoa - um canal chamado "titularis" que traria, por construção,
 * quase todas as contas e quase toda a receita.
 *
 * O caso não é hipotético: ele nasce **junto** com a captura no app. Enquanto
 * só a landing capturava, o app nunca escrevia nada; passando a capturar, todo
 * visitante que atravessa se auto-indicaria no momento em que o cookie
 * compartilhado não chegasse - navegador com armazenamento particionado, aba
 * anônima, navegador embutido de aplicativo. E aí o valor gravado seria
 * **errado**, e não vazio, que é bem pior: vazio a operação lê como "não sei",
 * errado ela lê como um canal que existe.
 */
function ehNossoDominio(host: string): boolean {
  const nosso = globalThis.location?.hostname;
  if (!nosso) return false;
  const deles = registravel(host);
  const daqui = registravel(nosso);
  // Sem domínio registrável dos dois lados (localhost), compara o host inteiro.
  return deles && daqui ? deles === daqui : host.toLowerCase() === nosso.toLowerCase();
}

function lerReferenciador(): string | null {
  try {
    const host = new URL(document.referrer).hostname || null;
    return host && !ehNossoDominio(host) ? host : null;
  } catch {
    return null;
  }
}

function dominioDoCookie(hostname: string): string | null {
  const dominio = registravel(hostname);
  return dominio ? `.${dominio}` : null;
}

/**
 * O que um referenciador conhecido quer dizer, quando não houve UTM.
 *
 * **Só vale para quem não declarou nada.** UTM escrita pela operação manda
 * sempre: se alguém montou `utm_source=instagram-stories`, foi porque quis
 * distinguir, e reduzir isso a "instagram" seria a ferramenta decidindo o que a
 * operação quis dizer.
 *
 * O problema que isto resolve é outro, e é do tráfego que ninguém etiqueta: o
 * orgânico. `www.google.com`, `www.google.com.br` e `google.com` são o mesmo
 * canal e entravam como três; `l.instagram.com` e `instagram.com` idem. Sem um
 * nome só, a pergunta que o SEO existe para responder - **quanto o orgânico
 * trouxe?** - não tem sujeito, porque orgânico não tinha nome: tinha uma lista
 * de hostnames.
 *
 * Host desconhecido continua **inteiro**. Encurtar `blog.exemplo.com.br` para
 * "exemplo" perderia qual página indicou, que é a única informação útil de uma
 * indicação; o que ele ganha é o `medium`, que antes era nulo.
 */
const POR_DOMINIO: Record<string, string> = {
  't.co': 'twitter',
  'x.com': 'twitter',
  't.me': 'telegram',
  'lnkd.in': 'linkedin',
  'fb.me': 'facebook',
  'fb.com': 'facebook',
  'youtu.be': 'youtube',
};

const BUSCA = new Set(['google', 'bing', 'duckduckgo', 'yahoo', 'ecosia', 'brave', 'yandex']);

const SOCIAL = new Set([
  'instagram', 'facebook', 'youtube', 'linkedin', 'twitter', 'tiktok',
  'telegram', 'whatsapp', 'reddit', 'pinterest', 'threads', 'kwai',
]);

interface Canal {
  source: string;
  medium: string;
}

function classificar(referrer: string | null): Canal | null {
  if (!referrer) return null;

  const dominio = registravel(referrer);
  const nome = (dominio && POR_DOMINIO[dominio]) ?? dominio?.split('.')[0] ?? null;

  if (nome && BUSCA.has(nome)) return { source: nome, medium: 'organic' };
  if (nome && SOCIAL.has(nome)) return { source: nome, medium: 'social' };
  return { source: referrer, medium: 'referral' };
}

/**
 * Os parâmetros de clique que os anunciantes penduram no link.
 *
 * Em ordem de precedência, e a ordem é a de quem paga: identificador de anúncio
 * vale mais que marca de compartilhamento. Eram dois - `fbclid` e `gclid` -, o
 * que deixava de fora o iOS do Google (`wbraid` e `gbraid`, que o substituem
 * quando não há consentimento de rastreamento), o TikTok, o Bing e, o mais
 * importante para nós, o **`igshid`**: o Instagram o pendura em todo link
 * compartilhado dali, então é ele que separa "veio do Instagram" de "veio sabe-
 * se lá de onde" no tráfego que ninguém etiquetou.
 */
const CLIQUES = ['fbclid', 'gclid', 'wbraid', 'gbraid', 'ttclid', 'msclkid', 'igshid'] as const;

/**
 * Tudo o que marca uma visita como vinda de campanha.
 *
 * **Exportada porque há um segundo leitor**, e ter duas listas seria ter duas
 * verdades: a landing repassa estas marcas nos links que levam ao app, para a
 * atribuição não depender só do cookie de domínio pai - que é o que falha
 * justamente no navegador embutido de aplicativo, por onde chega o tráfego de
 * grupo e de Instagram. Marca nova acrescentada aqui passa a viajar sozinha.
 */
export const MARCAS_DE_CAMPANHA = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', ...CLIQUES,
] as const;

function extrairOrigem(url: URL | null): Origem {
  const parametros = url?.searchParams;
  const parametro = (chave: string): string | null => parametros?.get(chave)?.trim() || null;
  const referrer = lerReferenciador();

  /*
   * A classificação só entra quando a operação **não** declarou a fonte. Com
   * `utm_source` escrito, o referenciador deixa de ser o canal - ele vira só o
   * lugar por onde o link estava - e derivar um `medium` dele contradiria quem
   * montou a campanha.
   */
  const canal = parametro('utm_source') ? null : classificar(referrer);

  return {
    source: normalizar(parametro('utm_source') ?? canal?.source ?? null),
    medium: normalizar(parametro('utm_medium') ?? canal?.medium ?? null),
    campaign: parametro('utm_campaign'),
    content: parametro('utm_content'),
    term: parametro('utm_term'),
    referrer,
    landing: url?.pathname ?? null,
    clickId: CLIQUES.reduce<string | null>((achado, chave) => achado ?? parametro(chave), null),
    firstSeenAt: new Date().toISOString(),
  };
}

function temCampanha(url: URL | null): boolean {
  return MARCAS_DE_CAMPANHA.some((chave) => Boolean(url?.searchParams.get(chave)?.trim()));
}

function textoOuNulo(valor: unknown): valor is string | null {
  return valor === null || typeof valor === 'string';
}

function desserializar(bruto: string | null): Origem | null {
  if (!bruto) return null;

  try {
    const dados = JSON.parse(bruto) as Partial<Origem> | null;
    if (!dados || typeof dados !== 'object') return null;
    const {
      source, medium, campaign, content, term, referrer, landing, clickId, firstSeenAt,
    } = dados;
    if (
      !textoOuNulo(source) ||
      !textoOuNulo(medium) ||
      !textoOuNulo(campaign) ||
      !textoOuNulo(content) ||
      !textoOuNulo(term) ||
      !textoOuNulo(referrer) ||
      !textoOuNulo(landing) ||
      !textoOuNulo(clickId) ||
      typeof firstSeenAt !== 'string' ||
      !Number.isFinite(Date.parse(firstSeenAt))
    ) return null;

    // O armazenamento pode vir de outra versão: campos extras não podem
    // carregar dados pessoais para o próximo cookie.
    return { source, medium, campaign, content, term, referrer, landing, clickId, firstSeenAt };
  } catch {
    return null;
  }
}

function ler(chave: string): Origem | null {
  // O cookie vence a cópia local porque só ele acompanha a pessoa entre origens.
  try {
    const prefixo = `${chave}=`;
    const cookie = document.cookie.split(';')
      .map((parte) => parte.trim())
      .find((parte) => parte.startsWith(prefixo));
    const origem = desserializar(cookie ? decodeURIComponent(cookie.slice(prefixo.length)) : null);
    if (origem) return origem;
  } catch {
    // Uma política de cookies não deve impedir a tentativa de ler a cópia local.
  }

  try {
    const origem = desserializar(globalThis.localStorage.getItem(chave));
    if (origem) return origem;
  } catch {
    // Com todo armazenamento bloqueado, ainda preservamos a origem nesta página.
  }

  const origem = memoria.get(chave);
  return origem ? { ...origem } : null;
}

function guardar(chave: string, origem: Origem, url: URL): void {
  memoria.set(chave, { ...origem });
  const bruto = JSON.stringify(origem);

  try {
    const dominio = dominioDoCookie(url.hostname);
    const validade = chave === PRIMEIRO_TOQUE ? `; Max-Age=${VALIDADE_EM_SEGUNDOS}` : '';
    const seguro = url.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${chave}=${encodeURIComponent(bruto)}; Path=/; SameSite=Lax`
      + validade + seguro + (dominio ? `; Domain=${dominio}` : '');
  } catch {
    // O navegador também pode recusar a escrita em silêncio; a cópia local
    // mantém o primeiro toque nas próximas visitas à mesma origem.
  }

  try {
    globalThis.localStorage.setItem(chave, bruto);
  } catch {
    // Falta de quota ou permissão não pode impedir a navegação nem a captura.
  }
}

export function primeiroToque(): Origem | null {
  return ler(PRIMEIRO_TOQUE);
}

export function toqueAtual(): Origem | null {
  const url = lerUrl();
  if (!temCampanha(url)) return primeiroToque();

  const origem = extrairOrigem(url);
  const guardado = ler(TOQUE_ATUAL);
  // Ler a mesma campanha de novo não deve alterar a data da captura.
  const campos = Object.keys(origem) as (keyof Origem)[];
  const mesmaOrigem = guardado && campos.every((campo) =>
    campo === 'firstSeenAt' || guardado[campo] === origem[campo]);
  return mesmaOrigem ? guardado : origem;
}

export function capturarOrigem(): Origem {
  const url = lerUrl();
  const primeiro = primeiroToque();
  const atual = toqueAtual() ?? extrairOrigem(url);
  if (!url) return atual;

  // O anúncio da volta não pode tomar o crédito do grupo que apresentou o
  // produto seis semanas antes, nem mudar a data em que a jornada começou.
  if (primeiro) memoria.set(PRIMEIRO_TOQUE, { ...primeiro });
  else guardar(PRIMEIRO_TOQUE, atual, url);
  guardar(TOQUE_ATUAL, atual, url);
  return atual;
}
